import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import api from '../services/api'

interface PriceSetting {
  id: string
  name: string
  pricePerHour: number
  startHour: number
  endHour: number
  dayOfWeek?: string
  isActive: boolean
}

const DAY_LABELS: { [key: string]: string } = {
  '': '全日',
  '0': '日曜日',
  '1': '月曜日',
  '2': '火曜日',
  '3': '水曜日',
  '4': '木曜日',
  '5': '金曜日',
  '6': '土曜日',
  'weekday': '平日',
  'weekend': '土日祝'
}

export default function AdminPrices() {
  const [prices, setPrices] = useState<PriceSetting[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    name: '',
    pricePerHour: '',
    startHour: '0',
    endHour: '24',
    dayOfWeek: ''
  })
  const { logout } = useAuth()

  useEffect(() => {
    fetchPrices()
  }, [])

  const fetchPrices = async () => {
    try {
      const response = await api.get('/prices')
      setPrices(response.data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await api.post('/prices', {
        name: form.name,
        pricePerHour: parseInt(form.pricePerHour),
        startHour: parseInt(form.startHour),
        endHour: parseInt(form.endHour),
        dayOfWeek: form.dayOfWeek || null
      })
      setForm({ name: '', pricePerHour: '', startHour: '0', endHour: '24', dayOfWeek: '' })
      setShowForm(false)
      fetchPrices()
    } catch (err: any) {
      alert(err.response?.data?.error || '追加に失敗しました')
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('この料金設定を削除しますか？')) return
    try {
      await api.delete(`/prices/${id}`)
      fetchPrices()
    } catch {
      alert('削除に失敗しました')
    }
  }

  if (loading) return <div style={styles.loading}>読み込み中...</div>

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>料金設定</h1>
        <div style={styles.headerButtons}>
          <a href="/admin" style={styles.backButton}>← ダッシュボード</a>
          <button onClick={logout} style={styles.logoutButton}>ログアウト</button>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>料金一覧</h2>
          <button onClick={() => setShowForm(!showForm)} style={styles.addButton}>
            {showForm ? 'キャンセル' : '+ 料金を追加'}
          </button>
        </div>

        {showForm && (
          <div style={styles.formCard}>
            <h3 style={styles.formTitle}>新しい料金設定</h3>
            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label}>料金名</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  style={styles.input}
                  placeholder="例：通常料金、深夜料金"
                  required
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>1時間あたりの料金（円）</label>
                <input
                  type="number"
                  value={form.pricePerHour}
                  onChange={(e) => setForm({ ...form, pricePerHour: e.target.value })}
                  style={styles.input}
                  placeholder="例：1000"
                  required
                />
              </div>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>開始時間（時）</label>
                  <input
                    type="number"
                    value={form.startHour}
                    onChange={(e) => setForm({ ...form, startHour: e.target.value })}
                    style={styles.input}
                    min="0"
                    max="23"
                    required
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>終了時間（時）</label>
                  <input
                    type="number"
                    value={form.endHour}
                    onChange={(e) => setForm({ ...form, endHour: e.target.value })}
                    style={styles.input}
                    min="1"
                    max="24"
                    required
                  />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>適用曜日</label>
                <select
                  value={form.dayOfWeek}
                  onChange={(e) => setForm({ ...form, dayOfWeek: e.target.value })}
                  style={styles.input}
                >
                  {Object.entries(DAY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <button type="submit" style={styles.submitButton}>追加する</button>
            </form>
          </div>
        )}

        {prices.length === 0 ? (
          <p style={styles.empty}>料金設定がありません。追加してください。</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>料金名</th>
                <th style={styles.th}>1時間あたり</th>
                <th style={styles.th}>時間帯</th>
                <th style={styles.th}>適用曜日</th>
                <th style={styles.th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {prices.map((price, idx) => (
                <tr key={price.id} style={{ backgroundColor: idx % 2 === 1 ? '#f9f9f9' : 'white' }}>
                  <td style={styles.td}>{price.name}</td>
                  <td style={styles.td}>¥{price.pricePerHour.toLocaleString()}</td>
                  <td style={styles.td}>{price.startHour}:00 〜 {price.endHour}:00</td>
                  <td style={styles.td}>{DAY_LABELS[price.dayOfWeek || ''] || price.dayOfWeek}</td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleDelete(price.id)}
                      style={styles.deleteButton}
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '20px', maxWidth: '1000px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  headerButtons: { display: 'flex', gap: '12px', alignItems: 'center' },
  title: { color: '#1F4E79', margin: 0 },
  backButton: { padding: '8px 16px', backgroundColor: '#3498db', color: 'white', borderRadius: '4px', textDecoration: 'none', fontSize: '14px' },
  logoutButton: { padding: '8px 16px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  section: { backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  sectionTitle: { color: '#333', margin: 0 },
  addButton: { padding: '8px 16px', backgroundColor: '#1F4E79', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  formCard: { backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '20px', marginBottom: '20px', border: '1px solid #eee' },
  formTitle: { color: '#333', marginTop: 0 },
  field: { marginBottom: '16px', flex: 1 },
  row: { display: 'flex', gap: '16px' },
  label: { display: 'block', marginBottom: '4px', color: '#555', fontSize: '14px' },
  input: { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box' },
  submitButton: { padding: '10px 24px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { backgroundColor: '#1F4E79', color: 'white', padding: '10px', textAlign: 'left', fontSize: '14px' },
  td: { padding: '10px', borderBottom: '1px solid #eee', fontSize: '14px' },
  deleteButton: { padding: '4px 12px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  loading: { textAlign: 'center', padding: '40px' },
  empty: { color: '#888', textAlign: 'center', padding: '20px' }
}
